import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Button, Modal, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import CrossIcon from "../../../assets/img/file-cut-icon.png";
import { useDispatch } from "react-redux";
import useCredentialData from "../../../hooks/useCredentialData";
import { createCredentials } from "../../../redux/slice/createCredentialSlice";
import SearchSelect from "../../../components/forms/SearchSelect";
import { useDocumentUpload } from "../../../hooks/useDocumentUpload";
import ValidateFile from "utils/ValidateFile";
import { useForm } from "react-hook-form";
export default function CreateCredentialModal({
  show,
  onHide,
  canEdit,
  candidate,
  hasRolePermission
}) {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    trigger,
    clearErrors, control,
  } = useForm();
  const [name] = useState(candidate?.name || "");
  const [candId] = useState(candidate?.id ? `cand-0${candidate.id}` : "");
  const { workingModeShift, designation, roles, department, activeEmp } =
    useCredentialData(show && hasRolePermission);
  const {
    selectedFiles,
    uploadStatus,
    handleFileSelect,
    handleFileUpload,
    handleViewFile,
    resetAllUploads,
  } = useDocumentUpload({ candidateId: candidate.id });

  useEffect(() => {
    if (uploadStatus["offerLetter"] === "succeeded") {
      clearErrors("offerLetter");
    }
  }, [uploadStatus["offerLetter"], clearErrors]);

  // If the user lacks role-view permission, intercept and show an access restriction pop-up
  if (show && !hasRolePermission) {
    return (
      <Modal show={show} onHide={onHide} size="md">
        <Modal.Header>
          <Modal.Title className="td-flex align-items-center mb-0">Permission Denied</Modal.Title>
          <button
            onClick={onHide}
            className="modal-close"
            aria-label="Close"
          >
            <img
              src={CrossIcon}
              alt="Close"
              style={{ width: "20px", height: "20px" }}
            />
          </button>
        </Modal.Header>
        <Modal.Body>
          <p className="fw-medium">
            You do not have the <strong>role-view</strong> permission, so you are unable to convert this candidate to an employee.
          </p>
        </Modal.Body>
        {/* <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer> */}
      </Modal>
    );
  }

  const employmentTypeOptions = [
    { value: "PERMANENT", label: "Permanent" },
    { value: "CONTRACTUAL", label: "Contractual" },
    { value: "PROBATION", label: "Probation" },
    { value: "CONTINGENT", label: "Contingent" },
    { value: "TRAINEE", label: "Trainee" },
    { value: "INTERN", label: "Intern" },
  ];

  const roleOptions = roles?.map((item) => ({
    value: item.roleId,
    label: item.roleName,
  }));
  const desgOptions = designation?.map((item) => ({
    value: typeof item === "string" ? item : item.name,
    label: typeof item === "string" ? item : item.name,
  })) || [];

  const deptOptions = department?.map((item) => ({
    value: typeof item === "string" ? item : item.name,
    label: typeof item === "string" ? item : item.name,
  })) || [];

  // const desgOptions = designation?.map((item) => ({
  //   value: item,
  //   label: item,
  // }));
  // const deptOptions = department?.map((item) => ({
  //   value: item,
  //   label: item,
  // }));
  const managerOptions = activeEmp
    ?.filter((item) => item.roleName !== "Employee")
    .map((item) => ({
      // value: `${item.email} - ${item.roleName}`,
      // label: `${item.email} - ${item.roleName}`,
      value: `${item.email}`,
      label: `${item.email}`,
    }));
  const teamLeadOptions = activeEmp
    ?.filter((item) => item.roleName !== "Employee")
    .map((item) => ({
      // value: `${item.email} - ${item.roleName}`,
      // label: `${item.email} - ${item.roleName}`,
      value: `${item.email}`,
      label: `${item.email}`,
    }));
  const workingModeOptions = workingModeShift?.WorkingMode?.map((item) => ({
    value: item,
    label: item,
  }));
  const shiftTimeOptions = workingModeShift?.ShiftType?.map((item) => ({
    value: item,
    label: item,
  }));
  const JobLocationOptions = [
    { value: "noida", label: "Noida" },
    { value: "gurgaon", label: "Gurgaon" },
  ];

  // Logic to determine button label based on upload status
  const renderUploadButtonContent = () => {
    const status = uploadStatus["offerLetter"];

    if (status === "loading") {
      return "Uploading...";
    }

    if (status === "succeeded") {
      return (
        <span className="d-inline-flex align-items-center">
          Uploaded{" "}
          <i
            className="bi bi-check ms-1"
            style={{ fontSize: "22px", lineHeight: "1" }}
          ></i>
        </span>
      );
    }

    return "Upload";
  };

  const handleCloseModal = () => {
    resetAllUploads();
    reset({ offerLetter: "" });
    onHide();
  };

  const handleCreateCredential = async (data) => {
    if (!canEdit) {
      toast.error("You do not have permission to create the credential!", {
        className: "toast-error-custom",
        autoClose: 5000,
      });
      return;
    }
    if (uploadStatus["offerLetter"] !== "succeeded") {
      setError("offerLetter", {
        type: "manual",
        message: "Please upload the offer letter before converting.",
      });
      return;
    }
    const payload = {
      designation: data.desg,
      reportManager: data.reportingManager,
      teamLead: data.teamLead,
      department: data.dept,
      officeLocation: data.jobLocation,
      workingMode: data.workingMode,
      shiftTime: data.shiftTime,
      aboutUs: data.abtEmp,
      officialEmailId: data.email,
      offerLetterUrl: "",
      userId: candidate.id,
      roleId: Number(data.roleId),
      empType: data.employmentType,
    };
    try {
      const response = await dispatch(createCredentials(payload)).unwrap();

      if (response?.data?.success === true) {
        toast.success(
          response?.data?.reason ||
          "Credentials has been created successfully!",
          {
            className: "toast-success-custom",
          },
        );
      } else {
        toast.error(
          response?.data?.reason || "Failed in creating Credentials!",
          { className: "toast-error-custom", autoClose: 8000 },
        );
      }
      resetAllUploads();
      reset({ offerLetter: "" });
      onHide();
    } catch (err) {
      toast.error(err?.data?.reason || "Unable to connect to server", {
        className: "toast-error-custom",
        autoClose: 8000,
      });
    }
  };

  return (
    <Modal show={show} onHide={handleCloseModal} size="lg">
      <Modal.Header>
        <Modal.Title>Convert to Employee</Modal.Title>
        <button onClick={onHide} className="modal-close">
          <img
            src={CrossIcon}
            alt="Close"
            style={{ width: "20px", height: "20px" }}
          />
        </button>
      </Modal.Header>
      <Form onSubmit={handleSubmit(handleCreateCredential)} className="convert_form">
        <Modal.Body>
          <div className="row">
            <div className="col-md-6 ">
              <Form.Group>
                <Form.Label>
                  Candidate Name <span className="star">*</span>
                </Form.Label>
                <Form.Control type="text" value={name} readOnly />
              </Form.Group>
            </div>
            <div className="col-md-6 ">
              <Form.Group>
                <Form.Label>
                  Candidate ID <span className="star">*</span>
                </Form.Label>
                <Form.Control type="text" value={candId} readOnly />
              </Form.Group>
            </div>
            <div className="col-md-6 ">
              <Form.Group >
                <Form.Label>
                  Official Email ID <span className="star">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  {...register("email", {
                    required: "Official email id is required",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@moptra\.com$/,
                      message: "Please enter the valid domain (@moptra.com)",
                    },
                  })}
                  placeholder="Please enter the official email id"
                />
                {errors.email && (
                  <div className="dept-error-message">{errors.email.message}</div>
                )}
              </Form.Group></div>
            <div className="col-md-6 ">
              <SearchSelect
                name="employmentType"
                label={
                  <>
                    Employment Type <span className="star">*</span>
                  </>
                }
                options={employmentTypeOptions}
                rules={{ required: "Employment Type is required" }}
                placeholder="Please select the Employment Type"
                control={control}
                errors={errors}
              /></div>
            <div className="col-md-6 ">
              <SearchSelect
                name="roleId"
                label={
                  <>
                    Role<span className="star">*</span>
                  </>
                }
                options={roleOptions}
                rules={{ required: "Role is required" }}
                placeholder="Please select the role"
                control={control}
                errors={errors}
              />
            </div>
            <div className="col-md-6 ">
              <SearchSelect
                name="desg"
                label={
                  <>
                    Designation<span className="star">*</span>
                  </>
                }
                options={desgOptions}
                rules={{ required: "Designation is required" }}
                placeholder="Please select the designation"
                control={control}
                errors={errors}
              />
            </div>
            <div className="col-md-6 ">
              <SearchSelect
                name="dept"
                label={
                  <>
                    Department<span className="star">*</span>
                  </>
                }
                options={deptOptions}
                rules={{ required: "Department is required" }}
                placeholder="Please select the department"
                control={control}
                errors={errors}
              /></div>
            <div className="col-md-6 ">
              <SearchSelect
                name="reportingManager"
                label={
                  <>
                    Reporting Manager<span className="star">*</span>
                  </>
                }
                options={managerOptions}
                rules={{ required: "Reporting Manager is required" }}
                placeholder="Please select the reporting manager"
                control={control}
                errors={errors}
              /></div>
            <div className="col-md-6 ">
              <SearchSelect
                name="teamLead"
                label={
                  <>
                    Team Lead <span className="star">*</span>
                  </>
                }
                options={teamLeadOptions}
                rules={{ required: "Team Lead is required" }}
                placeholder="Please select the team lead"
                control={control}
                errors={errors}
              />
            </div>
            <div className="col-md-6 ">
              <SearchSelect
                name="workingMode"
                label={
                  <>
                    Working Mode <span className="star">*</span>
                  </>
                }
                options={workingModeOptions}
                rules={{ required: "Working Mode is required" }}
                placeholder="Please select the working mode"
                control={control}
                errors={errors}
              /></div>
            <div className="col-md-6 ">
              <SearchSelect
                name="shiftTime"
                label={
                  <>
                    Shift
                  </>
                }
                options={shiftTimeOptions}
                //rules={{ required: "Shift Timing is required" }}
                placeholder="Please select the shift timing"
                control={control}
                errors={errors}
              /></div>
            <div className="col-md-6 ">
              <SearchSelect
                name="jobLocation"
                label={
                  <>
                    Job Location <span className="star">*</span>
                  </>
                }
                options={JobLocationOptions}
                rules={{ required: "Job Location is required" }}
                placeholder="Please select the job location"
                control={control}
                errors={errors}
              /></div>
            <div className="col-md-6 ">
              <Form.Group >
                <Form.Label>
                  About Employee
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  {...register("abtEmp", {
                    //required: "About Employee is required",
                    minLength: {
                      value: 10,
                      message:
                        "Please provide a bit more detail (at least 20 characters)",
                    },
                    maxLength: {
                      value: 500,
                      message: "Maximum 500 characters allowed",
                    },
                  })}
                />
                {errors.abtEmp && (
                  <div className="dept-error-message">{errors.abtEmp.message}</div>
                )}
              </Form.Group></div>
            <div className="col-md-6 ">
              <Form.Group >
                <Form.Label>
                  Upload Offer Letter <span className="star">*</span>
                </Form.Label>
                <div className="d-flex align-items-start">
                  <Form.Control
                    type="file"
                    style={{ marginRight: "8px" }}
                    accept="application/pdf"
                    placeholder="Please upload the offer letter"
                    {...register("offerLetter", {
                      required: "Uploading offer letter is required",
                      validate: (files) => ValidateFile(files, "application/pdf"),
                    })}
                    onChange={async (e) => {
                      await register("offerLetter").onChange(e);
                      handleFileSelect("offerLetter", e.target.files[0]);
                      if (errors.offerLetter?.type === "manual") {
                        clearErrors("offerLetter");
                      }
                      trigger("offerLetter");
                    }}
                  />
                  <button
                    type="button"
                    className="upload-file-btn"
                    disabled={
                      !selectedFiles.offerLetter ||
                      uploadStatus["offerLetter"] === "succeeded" ||
                      (errors.offerLetter && errors.offerLetter.type === "validate")
                    }
                    onClick={() => handleFileUpload("offerLetter")}
                  >
                    {renderUploadButtonContent()}
                  </button>
                </div>
                {uploadStatus["offerLetter"] === "succeeded" && (
                  <button
                    type="button"
                    className="view-btn"
                    onClick={() => handleViewFile("offerLetter")}
                  >
                    View uploaded offer letter
                  </button>
                )}
                {errors.offerLetter && (
                  <div className="dept-error-message">
                    {errors.offerLetter.message}
                  </div>
                )}
              </Form.Group>
            </div>

          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="cancel-cta" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="update-cta" type="submit" disabled={!canEdit}>
            Convert
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

CreateCredentialModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  canEdit: PropTypes.bool,
  hasRolePermission: PropTypes.bool,
  candidate: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
};
